import UploadFileIcon from '@mui/icons-material/UploadFile';
import {
  Alert,
  Box,
  Button,
  Chip,
  Dialog,
  DialogActions,
  DialogContent,
  DialogTitle,
  LinearProgress,
  MenuItem,
  Paper,
  Stack,
  Table,
  TableBody,
  TableCell,
  TableContainer,
  TableHead,
  TableRow,
  TextField,
  Typography,
} from '@mui/material';
import { useMemo, useState } from 'react';

import { importContacts, previewContactImport } from '../../api/business/endpoints';
import type {
  ContactImportOutcome,
  ContactImportPreview,
  ContactImportResult,
  ContactImportRow,
} from '../../api/business/types';
import { useAppMutation } from '../../api/useAppMutation';
import { ErrorNotice } from '../../components/ErrorNotice';
import { ServerPagination } from '../../components/ServerPagination';
import { SortableCell } from '../../components/SortableCell';
import { TableFilters } from '../../components/TableFilters';
import { sortRows, useSort } from '../../components/useSort';
import { useT } from '../../i18n/LanguageContext';

const PAGE_SIZE = 50;

const OUTCOMES: Array<ContactImportOutcome> = ['CREATE', 'UPDATE', 'ERROR'];

const OUTCOME_COLOURS: Record<ContactImportOutcome, 'success' | 'info' | 'error'> = {
  CREATE: 'success',
  UPDATE: 'info',
  ERROR: 'error',
};

type RowSortKey = 'position' | 'name' | 'outcome';

/**
 * Import contacts from a ZIP (R0039 D-19): the file is checked first and every row shown — new, fills in a contact
 * (and found how), or refused and why; only then imported, the same file sent again. The preview holds its whole
 * list, so it sorts and pages in the browser.
 */
export const ContactImportDialog = ({ companyId, onClose }: { companyId: string; onClose: () => void }) => {
  const t = useT();
  const [file, setFile] = useState<File | null>(null);
  const [progress, setProgress] = useState(0);
  const [preview, setPreview] = useState<ContactImportPreview | null>(null);
  const [result, setResult] = useState<ContactImportResult | null>(null);
  const [outcome, setOutcome] = useState<ContactImportOutcome | ''>('');
  const [term, setTerm] = useState('');
  const [page, setPage] = useState(0);
  const sort = useSort<RowSortKey>('position', 'asc', () => setPage(0));

  const check = useAppMutation(
    (chosen: File) => previewContactImport(companyId, chosen, setProgress),
    [],
    (answer: ContactImportPreview) => setPreview(answer),
  );
  const apply = useAppMutation(
    (chosen: File) => importContacts(companyId, chosen, setProgress),
    ['contacts', 'contact', 'contact-labels', 'contact-notes', 'contact-history'],
    (answer: ContactImportResult) => setResult(answer),
  );

  const rows: Array<ContactImportRow> = useMemo(() => {
    const needle = term.trim().toLowerCase();
    const filtered = (preview?.rows ?? []).filter(
      (row) =>
        (outcome === '' || row.outcome === outcome) &&
        (needle === '' ||
          [row.name, row.primaryEmail, row.ico].some((value) =>
            (value ?? '').toLowerCase().includes(needle),
          )),
    );

    return sortRows(filtered, sort.direction, (row) =>
      sort.key === 'position' ? row.position : sort.key === 'name' ? row.name : row.outcome,
    );
  }, [preview, outcome, term, sort.key, sort.direction]);

  const choose = (chosen: File | null): void => {
    setFile(chosen);
    setPreview(null);
    setResult(null);
    setProgress(0);
  };

  const detailOf = (row: ContactImportRow): string =>
    row.outcome === 'ERROR'
      ? row.problems
          .map((problem) =>
            t(`contactImport.problem.${problem.code}` as const, { value: problem.value ?? '' }),
          )
          .join(' ')
      : row.outcome === 'UPDATE' && row.matchedBy !== null
        ? `${t(`contactImport.matchedBy.${row.matchedBy}` as const)}${row.contactId === null ? ` · ${t('contactImport.aboveRow')}` : ''}`
        : '';

  const isBusy = check.isPending || apply.isPending;

  return (
    <Dialog open onClose={isBusy ? undefined : onClose} fullWidth maxWidth="lg">
      <DialogTitle>{t('contactImport.title')}</DialogTitle>
      <DialogContent>
        <Stack spacing={2} sx={{ pt: 1 }}>
          <Typography variant="body2" color="text.secondary">
            {t('contactImport.intro')}
          </Typography>
          <Stack direction="row" spacing={1.5} alignItems="center">
            <Button component="label" variant="outlined" startIcon={<UploadFileIcon />} disabled={isBusy}>
              {t('contactImport.choose')}
              <input
                hidden
                type="file"
                accept=".zip,application/zip"
                onChange={(event) => choose(event.target.files?.[0] ?? null)}
              />
            </Button>
            <Typography variant="body2">{file?.name ?? ''}</Typography>
          </Stack>
          {isBusy ? (
            <Box>
              <LinearProgress
                variant={progress < 1 ? 'determinate' : 'indeterminate'}
                value={progress * 100}
              />
              <Typography variant="caption" color="text.secondary">
                {t('contactImport.uploading', { percent: Math.round(progress * 100) })}
              </Typography>
            </Box>
          ) : null}
          <ErrorNotice error={check.error ?? apply.error} />
          {result !== null ? (
            <Alert severity="success" variant="outlined">
              {t('contactImport.done', {
                created: result.created,
                updated: result.updated,
                refused: result.refused,
                labels: result.labelsCreated,
              })}
            </Alert>
          ) : null}
          {preview !== null && result === null ? (
            <>
              <Alert severity={preview.errors > 0 ? 'warning' : 'info'} variant="outlined">
                <Box>
                  {t('contactImport.summary', {
                    total: preview.total,
                    creates: preview.creates,
                    updates: preview.updates,
                    errors: preview.errors,
                  })}
                </Box>
                {preview.labelsToCreate.length > 0 ? (
                  <Box>
                    {t('contactImport.labelsToCreate', { labels: preview.labelsToCreate.join(', ') })}
                  </Box>
                ) : null}
                <Box sx={{ mt: 0.5 }}>{t('contactImport.rules')}</Box>
              </Alert>
              <Paper variant="outlined">
                <TableFilters>
                  <TextField
                    size="small"
                    label={t('contactImport.searchRows')}
                    value={term}
                    onChange={(event) => {
                      setTerm(event.target.value);
                      setPage(0);
                    }}
                    sx={{ minWidth: 240 }}
                  />
                  <TextField
                    select
                    size="small"
                    label={t('contactImport.outcome')}
                    value={outcome}
                    onChange={(event) => {
                      setOutcome(event.target.value as ContactImportOutcome | '');
                      setPage(0);
                    }}
                    sx={{ minWidth: 160 }}
                  >
                    <MenuItem value="">{t('common.all')}</MenuItem>
                    {OUTCOMES.map((one) => (
                      <MenuItem key={one} value={one}>
                        {t(`contactImport.outcome.${one}` as const)}
                      </MenuItem>
                    ))}
                  </TextField>
                </TableFilters>
                <TableContainer sx={{ maxHeight: 420 }}>
                  <Table size="small" stickyHeader>
                    <TableHead>
                      <TableRow>
                        <SortableCell sort={sort} column="position">
                          {t('contactImport.position')}
                        </SortableCell>
                        <SortableCell sort={sort} column="name">
                          {t('contacts.name')}
                        </SortableCell>
                        <TableCell>{t('contacts.email')}</TableCell>
                        <TableCell>{t('contacts.ico')}</TableCell>
                        <SortableCell sort={sort} column="outcome">
                          {t('contactImport.outcome')}
                        </SortableCell>
                        <TableCell>{t('contactImport.detail')}</TableCell>
                      </TableRow>
                    </TableHead>
                    <TableBody>
                      {rows.slice(page * PAGE_SIZE, (page + 1) * PAGE_SIZE).map((row) => (
                        <TableRow key={row.position}>
                          <TableCell>{row.position}</TableCell>
                          <TableCell>{row.name ?? t('common.none')}</TableCell>
                          <TableCell sx={{ overflowWrap: 'anywhere', minWidth: 180 }}>
                            {row.primaryEmail ?? t('common.none')}
                          </TableCell>
                          <TableCell>{row.ico ?? t('common.none')}</TableCell>
                          <TableCell>
                            <Chip
                              size="small"
                              variant="outlined"
                              color={OUTCOME_COLOURS[row.outcome]}
                              label={t(`contactImport.outcome.${row.outcome}` as const)}
                            />
                          </TableCell>
                          <TableCell>
                            <Typography variant="caption" color="text.secondary">
                              {detailOf(row)}
                            </Typography>
                          </TableCell>
                        </TableRow>
                      ))}
                    </TableBody>
                  </Table>
                </TableContainer>
                <ServerPagination
                  page={page}
                  pageSize={PAGE_SIZE}
                  total={rows.length}
                  onPageChange={setPage}
                />
              </Paper>
            </>
          ) : null}
        </Stack>
      </DialogContent>
      <DialogActions sx={{ px: 3, pb: 2 }}>
        <Button onClick={onClose} disabled={isBusy}>
          {result !== null ? t('common.close') : t('common.cancel')}
        </Button>
        {result === null && preview === null ? (
          <Button
            variant="contained"
            disabled={file === null || isBusy}
            onClick={() => file && check.mutate(file)}
          >
            {t('contactImport.check')}
          </Button>
        ) : null}
        {result === null && preview !== null ? (
          <Button
            variant="contained"
            disabled={file === null || isBusy || preview.creates + preview.updates === 0}
            onClick={() => file && apply.mutate(file)}
          >
            {t('contactImport.confirm', { count: preview.creates + preview.updates })}
          </Button>
        ) : null}
      </DialogActions>
    </Dialog>
  );
};
